import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface SelectOption {
  value: string;
  label: string;
}

interface Pos {
  left: number;
  width: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
}

// Custom dropdown so the OPEN list matches the app (native <select> popups are
// unstyleable OS chrome). The menu renders in a PORTAL on document.body so it's
// never clipped by a scrolling modal: on desktop it's anchored to the control
// (flipping up when there's no room below and capping its height to fit); on
// phones it's a floating bottom sheet.
export default function Select({
  value,
  onChange,
  options,
  placeholder = "—",
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [pos, setPos] = useState<Pos | null>(null);
  const ctrlRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const selected = options.find((o) => o.value === value);

  function place() {
    const el = ctrlRef.current;
    if (!el) return;
    const isSheet = window.innerWidth <= 720;
    setSheet(isSheet);
    if (isSheet) {
      setPos(null);
      return;
    }
    const r = el.getBoundingClientRect();
    const gap = 6;
    const spaceBelow = window.innerHeight - r.bottom - gap - 8;
    const spaceAbove = r.top - gap - 8;
    const desired = Math.min(300, options.length * 44 + 16);
    const dropUp = desired > spaceBelow && spaceAbove > spaceBelow;
    const maxHeight = Math.max(140, Math.min(desired, dropUp ? spaceAbove : spaceBelow));
    setPos(
      dropUp
        ? { left: r.left, width: r.width, maxHeight, bottom: window.innerHeight - r.top + gap }
        : { left: r.left, width: r.width, maxHeight, top: r.bottom + gap },
    );
  }

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onMove = () => place();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ctrlRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const menu =
    open &&
    createPortal(
      <>
        {sheet && <div className="select__scrim" onClick={() => setOpen(false)} />}
        <ul
          ref={menuRef}
          className={"select__menu" + (sheet ? " select__menu--sheet" : "")}
          role="listbox"
          style={
            sheet
              ? undefined
              : pos
                ? { left: pos.left, width: pos.width, maxHeight: pos.maxHeight, top: pos.top, bottom: pos.bottom }
                : { visibility: "hidden" }
          }
        >
          {options.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={"select__option" + (o.value === value ? " is-selected" : "")}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
                {o.value === value && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            </li>
          ))}
        </ul>
      </>,
      document.body,
    );

  return (
    <div className={"select" + (open ? " is-open" : "")}>
      <button
        ref={ctrlRef}
        type="button"
        className="input select__control"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={"select__value" + (selected ? "" : " is-placeholder")}>
          {selected ? selected.label : placeholder}
        </span>
        <svg className="select__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {menu}
    </div>
  );
}
